import Plot from 'react-plotly.js';
import { useEffect, useState} from 'react';
import { chartColors } from '../helpers/chartColors';
import { formatUwi } from '../helpers/formatUwi';
import { convertVolume } from '../helpers/convertVolume';
import { normalizeVolume } from '../helpers/normalizeVolume';

const MONTH_LENGTH = 30.436875;
const PRODUCTION_CUTOFF = 0.01;

export const ViewCDGRJobChart = ({ darkMode, demoUser, well, showOutliers, chartType, chartTime, typeCurve, groupWell, previousGroupWell, currentTab, options }) => {

  const useGetData = (options, well, showOutliers, chartType, chartTime, typeCurve, groupWell, previousGroupWell, currentTab) => {
    const [data, setData] = useState([]);

    useEffect(() => {
      let active = true;
  
      const fluids = options?.fluids;

      active && plotFluids(fluids);

      async function plotFluids(fluids) {
        try {
          let fluidsData = [];

          for (let fluidIndex in fluids) {
            const fluid = fluids[fluidIndex];
            const fluidData = await getForecasts(fluid, Number(fluidIndex)+1);
            if (fluidData && fluidData?.length > 0) {
              fluidsData = fluidsData.concat(fluidData);
            };
          };
          setData(fluidsData);
          
        } catch (error) {
          console.log(error);
        };
      };

  
      return () => active = false;
  
      // calculate well forecasts
      async function getForecasts(fluid, numFluid) {
        try {
          const production = (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? well?.production : well?.production?.previousProduction;
          const previousParams = (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? well?.job?.byFluid?.[fluid]?.previousParams : {};
          const previousForecast = (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? well?.job?.byFluid?.[fluid]?.previousForecast?.filter(f => f.monthsOnline >= previousParams?.ti) : [];
          const newParams = (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? well?.job?.byFluid?.[fluid]?.newParams : {};
          const newForecast = (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? well?.job?.byFluid?.[fluid]?.newForecast?.filter(f => f.monthsOnline >= newParams?.ti) : [];
          const previousTypeCurveProduction = typeCurve?.production?.previousProduction;
          const previousTypeCurveForecast = typeCurve?.job?.byFluid?.[fluid]?.previousForecast;
          const newTypeCurveProduction = typeCurve?.production?.newProduction;
          const newTypeCurveForecast = typeCurve?.job?.byFluid?.[fluid]?.newForecast;
          const groupWellForecast = currentTab === 3 && groupWell?.forecast;
          const previousGroupWellProduction = currentTab === 3 && previousGroupWell?.production;
          const previousGroupWellForecast = currentTab === 3 && previousGroupWell?.forecast;

          if (!active) return;

          let traces = [];
          
          let XProd = [];
          let YProd = [];
          production?.byFluid?.[fluid]?.forEach(d => {
            if (d.volume / MONTH_LENGTH > PRODUCTION_CUTOFF) {
              let volume = convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH;
              if (options.normalization !== 'off') {
                volume = normalizeVolume(volume, options.normalization, typeCurve?.details, well?.completions);
              };
              YProd.push(volume);
              XProd.push(chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : (convertVolume(d.cumVolume, 'field', fluid) / 1000));
              XProd = XProd.sort((a, b) => a - b);
            };
          });

          if (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) { // if well
            
            if (options?.fluidRatio === 'CGR' && (options.fluids?.length === 1 || (options.fluids?.length === 2 && fluid === options?.fluid)) && options?.chartType === 'Rate-Time') { // Plot CGR
              let XCGR = [];
              let YCGR = [];
              well?.fluidRatios?.['CGR']?.forEach(r => {
                if (r.ratio > 0.0001) {
                  YCGR.push(r.ratio * 6.29 / 35.3 * 1000); // convert to field
                  XCGR.push(chartType === 'Rate-Time' ? 
                    (chartTime === 'Relative' ? r.monthsOnline : new Date(r.date)) : 
                    convertVolume(well.production?.byFluid?.[fluid]?.find(p => (new Date(p.date)?.getFullYear() === new Date(r.date)?.getFullYear() && new Date(p.date)?.getMonth() === new Date(r.date)?.getMonth()))?.cumVolume, 'field', fluid) / 1000);
                };
              });
              XCGR = XCGR.sort((a, b) => a - b);
              traces.push({
                x: XCGR,
                y: YCGR,
                yaxis: 'y2',
                xaxis: 'x1',
                name: 'CGR',
                type: 'scatter',
                fill: 'tozeroy',
                opacity: 0.5,
                mode: 'line',
                line: {
                  width: 1
                },
                marker: {color: chartColors[3]}
              });
            };

            if (options?.showPressure === 'Show' && (options.fluids.length === 1 || (options.fluids.length === 2 && fluid === options?.fluid)) && options?.chartType === 'Rate-Time') { // Plot Pressure
              let X = [];
              let YTubing = [];
              let YCasing = [];
              production?.pressure?.forEach(p => {
                if (p.tubingPressure > 0 || p.casingPressure > 0) {
                  YTubing.push(p.tubingPressure || 0);
                  YCasing.push(p.casingPressure || 0);
                  X.push(chartType === 'Rate-Time' ?  (chartTime === 'Relative' ? p.monthsOnline : new Date(p.date)) : 0);
                };
              });

              traces.push(
                { // tubing pressure data
                  x: X.sort((a,b) => a - b),
                  y: YTubing,
                  mode: 'line',
                  type: 'scatter',
                  xaxis: 'x1',
                  yaxis: 'y3',
                  name: `Tubing Pressure (psi)`,
                  line: {
                    width: 2,
                    color: "#333333"
                  },
                }
              );

              traces.push(
                { // casing pressure data
                  x: X?.sort((a, b) => a - b),
                  y: YCasing,
                  mode: 'line',
                  type: 'scatter',
                  xaxis: 'x1',
                  yaxis: 'y3',
                  name: `Casing Pressure (psi)`,
                  line: {
                    width: 2,
                    color: "#333333",
                    dash: 'dash'
                  },
                }
              );
            };

            traces.push(
              { // historic data
                x: XProd,
                y: YProd,
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Production`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1]
                },
              }
            );

            const XForecastPrev = [];
            let YForecastPrev = [];
            previousForecast?.forEach(d => {
              if (d.volume?.p50 / MONTH_LENGTH > PRODUCTION_CUTOFF) {
                let volume = convertVolume(d.volume?.p50, 'field', fluid) / MONTH_LENGTH;
                if (options.normalization !== 'off') {
                  volume = normalizeVolume(volume, options.normalization, typeCurve?.details, well?.completions);
                };
                YForecastPrev.push(volume);
                XForecastPrev.push(chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : (convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000));
              };
            });       
            traces.push(
              { // previous forecast
                x: XForecastPrev,
                y: YForecastPrev,
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Prev. Forecast`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  dash: 'dash'
                },
              }
            );

            const XForecastNew = [];
            let YForecastNew = [];
            newForecast?.forEach(d => {
              if (d.volume?.p50 / MONTH_LENGTH > PRODUCTION_CUTOFF) {
                let volume = convertVolume(d.volume?.p50, 'field', fluid) / MONTH_LENGTH;
                if (options.normalization !== 'off') {
                  volume = normalizeVolume(volume, options.normalization, typeCurve?.details, well?.completions);
                };
                YForecastNew.push(volume);
                XForecastNew.push(chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : (convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000));
              };
            });
            traces.push(
              { // new forecast
                x: XForecastNew,
                y: YForecastNew,
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} New Forecast`,
                line: {
                  width: 2,
                  color: chartColors[0]
                },
              }
            );

            const XTCPrev = [];
            let YTCPrev = [];
            previousTypeCurveProduction?.byFluid?.[fluid]?.forEach(d => {
              if (d.volume / MONTH_LENGTH > PRODUCTION_CUTOFF) {
                let volume = convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH;
                YTCPrev.push(volume);
                XTCPrev.push(chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : (convertVolume(d.cumVolume, 'field', fluid) / 1000));
              };
            });
            traces.push(
              { // previous type curve data
                x: XTCPrev,
                y: YTCPrev,
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `TC ${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)}`,
                // name: `${typeCurve?.name || 'Type Curve'} ${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Previous`,
                line: {
                  width: 2,
                  color: 'grey',
                  dash: currentTab !== 2 && 'dot'
                },
              }
            );  

            const outliers = well?.job?.byFluid?.[fluid]?.newParams?.outliers;

            if (showOutliers) {
              const XOutliers = [];
              let YOutliers = [];
              outliers?.forEach(o => {
                if (o.value > 0.1) {
                  let volume = convertVolume(o.value, 'field', fluid) / MONTH_LENGTH;
                  if (options.normalization !== 'off') {
                    volume = normalizeVolume(volume, options.normalization, typeCurve?.details, well?.completions);
                  };
                  YOutliers.push(volume);
                  XOutliers.push(chartType === 'Rate-Time' ? (chartTime === 'Relative' ? o.monthsOnline : new Date(o.date)) : (convertVolume(o.cumValue, 'field', fluid) / 1000));
                };
              });
              traces.push({
                type: 'scatter',
                mode: 'markers',
                x: XOutliers,
                y: YOutliers,
                yaxis: 'y1',
                xaxis: 'x1',
                name: 'Outliers',
                marker: {
                  color: 'rgba(255,255,255,0.4)',
                  line: {
                    width: 1,
                    color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  },
                  symbol: 'circle',
                  size: 8
                },
                showlegend: numFluid === 1
              });
            };

          } else if (currentTab === 2) { // type curve
            
            if (options?.fluidRatio === 'CGR' && (options.fluids.length === 1 || (options.fluids.length === 2 && fluid === options?.fluid)) && options.chartType === 'Rate-Time') { // Plot CGR

              let XCGR = [];
              let YCGR = [];

              typeCurve?.fluidRatios?.['CGR']?.forEach(r => {
                if (r.ratio > 0.0001) {
                  YCGR.push(r.ratio * 6.29 / 35.3 * 1000); // convert to field
                  XCGR.push(chartType === 'Rate-Time' ? r.monthsOnline : 
                    convertVolume(typeCurve.job?.byFluid?.[fluid]?.newForecast?.find(p => (new Date(p.date)?.getFullYear() === new Date(r.date)?.getFullYear() && new Date(p.date)?.getMonth() === new Date(r.date)?.getMonth()))?.cumVolume, 'field', fluid) / 1000);
                    // convertVolume(typeCurve.job?.byFluid?.[fluid]?.newForecast?.find(p => p.monthsOnline === r.monthsOnline)?.cumVolume, 'field', fluid) / 1000);
                };
              });

              traces.push({
                x: XCGR,
                y: YCGR,
                yaxis: 'y2',
                xaxis: 'x1',
                name: 'CGR',
                type: 'scatter',
                fill: 'tozeroy',
                opacity: 0.5,
                mode: 'line',
                line: {
                  width: 1
                },
                marker: {color: chartColors[3]}
              });
            };
            
            traces.push(
              { // previous type curve data
                x: previousTypeCurveProduction?.byFluid?.[fluid]?.map(d => chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : convertVolume(d.cumVolume, 'field', fluid) / 1000),
                y: previousTypeCurveProduction?.byFluid?.[fluid]?.map(d => convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH) || [null],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Prev.`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  dash: 'dot'
                },
              }
            );
            traces.push(
              { // previous type curve forecast
                x: previousTypeCurveForecast?.map(d => chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000),
                y: previousTypeCurveForecast?.map(d => convertVolume(d.volume?.p50, 'field', fluid) / MONTH_LENGTH) || [null],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Prev. Forecast`,
                opacity: 0.5,
                line: {
                  width: 2,
                  color: "#333",
                  dash: 'dash'
                },
              }
            );
            traces.push(
              { // new type curve data
                x: newTypeCurveProduction?.byFluid?.[fluid]?.map(d => chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : convertVolume(d.cumVolume, 'field', fluid) / 1000),
                y: newTypeCurveProduction?.byFluid?.[fluid]?.map(d => convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH) || [null],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} New`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  // dash: 'dot'
                },
              }
            );
            traces.push(
              { // new type curve forecast
                x: newTypeCurveForecast?.map(d => chartType === 'Rate-Time' ? (chartTime === 'Relative' ? d.monthsOnline : new Date(d.date)) : convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000),
                y: newTypeCurveForecast?.map(d => convertVolume(d.volume?.p50, 'field', fluid) / MONTH_LENGTH) || [null],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} New Forecast`,
                opacity: 0.5,
                line: {
                  width: 2,
                  color: "#333",
                  // dash: 'dash'
                },
              }
            );
          } else if (currentTab === 3) { // group well
            // const gwProd = groupWellProduction?.byFluid?.[fluid]?.filter(d => d.volume / MONTH_LENGTH > PRODUCTION_CUTOFF);
            const gwForecast = groupWellForecast?.byFluid?.[fluid]?.filter(d => d.volume?.p50 / MONTH_LENGTH > PRODUCTION_CUTOFF);
            
            const prevGWProd = previousGroupWellProduction?.byFluid?.[fluid]?.filter(d => d.volume / MONTH_LENGTH > PRODUCTION_CUTOFF);
            const prevGWForecast = previousGroupWellForecast?.byFluid?.[fluid]?.filter(d => d.volume?.p50 / MONTH_LENGTH > PRODUCTION_CUTOFF);

            
            if (options?.fluidRatio === 'CGR' && (options.fluids.length === 1 || (options.fluids.length === 2 && fluid === options?.fluid))) { // Plot CGR

              let XCGR = [];
              let YCGR = [];

              groupWell?.fluidRatios?.['CGR']?.forEach(r => {
                if (r.ratio > 0.0001) {
                  YCGR.push(r.ratio * 6.29 / 35.3 * 1000); // convert to field
                  XCGR.push(chartType === 'Rate-Time' ? new Date(r.date) : 
                    convertVolume(groupWell.production?.byFluid?.[fluid]?.find(p => (new Date(p.date)?.getFullYear() === new Date(r.date)?.getFullYear() && new Date(p.date)?.getMonth() === new Date(r.date)?.getMonth()))?.cumVolume, 'field', fluid) / 1000);
                };
              });

              traces.push({
                x: XCGR,
                y: YCGR,
                yaxis: 'y2',
                xaxis: 'x1',
                name: 'CGR',
                type: 'scatter',
                fill: 'tozeroy',
                opacity: 0.5,
                mode: 'line',
                line: {
                  width: 1
                },
                marker: {color: chartColors[3]}
              });
            };

            traces.push(
              {
                x: prevGWProd?.map(d => chartType === 'Rate-Time' ? new Date(d.date) : convertVolume(d.cumVolume, 'field', fluid) / 1000),
                y: prevGWProd?.map(d => convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH) || [PRODUCTION_CUTOFF],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Previous`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  dash: 'dot'
                },
              }
            );
            traces.push(
              {
                x: prevGWForecast?.map(d => chartType === 'Rate-Time' ? new Date(d.date) : convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000),
                y: prevGWForecast?.map(d => convertVolume(d.volume?.p50, 'field', fluid) / MONTH_LENGTH) || [PRODUCTION_CUTOFF],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} Prev. Forecast`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1],
                  dash: 'dot'
                },
                showlegend: false
              }
            );

            // traces.push(
            //   {
            //     x: gwProd?.map(d => chartType === 'Rate-Time' ? new Date(d.date) : convertVolume(d.cumVolume, 'field', fluid) / 1000),
            //     y: gwProd?.map(d => convertVolume(d.volume, 'field', fluid) / MONTH_LENGTH) || [PRODUCTION_CUTOFF],
            //     mode: 'line',
            //     type: 'scatter',
            //     xaxis: 'x1',
            //     yaxis: 'y1',
            //     name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} New`,
            //     line: {
            //       width: 2,
            //       color: fluid === 'gas' ? chartColors[6] : chartColors[1]
            //     },
            //   }
            // );
            traces.push(
              {
                x: gwForecast?.map(d => chartType === 'Rate-Time' ? new Date(d.date) : convertVolume(d.cumVolume?.p50, 'field', fluid) / 1000),
                y: gwForecast?.map(d => convertVolume(d.volume?.p50, 'field', fluid)  / MONTH_LENGTH) || [PRODUCTION_CUTOFF],
                mode: 'line',
                type: 'scatter',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${fluid?.charAt(0)?.toUpperCase()+fluid?.slice(1,)} New`,
                line: {
                  width: 2,
                  color: fluid === 'gas' ? chartColors[6] : chartColors[1]
                  // dash: 'dot'
                },
              }
            );
            
          };

          return traces;
        } catch (error) {
          console.log(error);
        };
      };
  
    }, [options, well, showOutliers, chartType, chartTime, typeCurve, groupWell, previousGroupWell, currentTab]);

    return data;

  };

  const data = useGetData(options, well, showOutliers, chartType, chartTime, typeCurve, groupWell, previousGroupWell, currentTab);

  const layout = {
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      title: {
        text: 'Production Rate (mscf/d or stb/d)'
      },
      type: 'log',
      anchor: 'x',
      // type: chartType === 'Rate-Time' && 'log',
      autorange: true,
      rangemode: 'nonnegative'
    },
    yaxis3: { // Pressure y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: 'transparent',
      title: {
        text: 'Pressure (psi)'
      },
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0,
      // type: chartType === 'Rate-Time' && 'log',
      autorange: true,
      rangemode: 'nonnegative'
    },
    yaxis2: { // CGR y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      showgrid: false,
      title: {
        text: 'CGR (stb/mmscf)'
      },
      type: chartType === 'Rate-Time' && 'log',
      autorange: true,
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'x'
    },
    xaxis: {
      linecolor: darkMode ? 'lightgrey' : 'grey',
      color: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      autorange: true,
      type: (chartTime === 'Absolute' || (currentTab === 3 && chartType === 'Relative')) && 'date',
      title: {
        text: chartType === 'Rate-Time' ? ((chartTime === 'Relative' && currentTab !== 3) ? 'Time (months)' : 'Date') : 'Cumulative Volume (mmscf or mstb)',
        standoff: 10
      },
      rangemode: 'nonnegative',
      domain: [((options?.showPressure === 'Show' && chartType === 'Rate-Time') ? 0.1 : 0), 1]
    },
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: -0.2,
    },
    hoverlabel: {align: 'auto', width: 50},
    uirevision:'true',
    margin: {"t": 50, "l": 60, "r": 70, "b": 60},
    shapes: [ 
      
    ],
    images: [
      {
        x: 0.055,
        y: 1.04,
        sizex: 0.1,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xanchor: "right",
        xref: "paper",
        yanchor: "bottom",
        yref: "paper",
        opacity: 1
      }
    ],
    title: {
      text: demoUser ? 'Demo' : currentTab === 2 ? well?.name : currentTab === 3 ? groupWell?.name : formatUwi(well?.uwi),
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 14
      }
    },
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: 'forecast-comparison',
      height: 540,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', marginLeft: 20}}
    />
  );

};
